import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { VideoPlayer } from "@/components/VideoPlayer";
import { Spinner, VideoCamera, ArrowRight } from "@phosphor-icons/react";

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const requested = params.get("cat") || "All";

  useEffect(() => { api.get("/videos").then(({ data }) => { setVideos(data); setLoading(false); }).catch(() => setLoading(false)); }, []);

  const cats = ["All", ...Array.from(new Set(videos.map((v) => v.category || "General")))];
  const cat = cats.includes(requested) ? requested : "All";
  const setCat = (c) => setParams(c === "All" ? {} : { cat: c }, { replace: true });
  const visible = videos.filter((v) => cat === "All" || (v.category || "General") === cat);
  const missing = !loading && requested !== "All" && !cats.includes(requested);

  return (
    <div className="min-h-screen bg-[#FAFAFB] text-zinc-950" data-testid="videos-page">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="videos-logo">
            <img src="/orbit-icon.jpg" alt="Orbit" className="h-9 w-9 object-contain rounded-md" />
            <span className="font-display font-bold text-sm tracking-tight"><span className="sm:hidden">Orbit</span><span className="hidden sm:inline">Orbit Inventory Management</span></span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/pricing"><Button variant="ghost" className="h-10 font-semibold hidden sm:inline-flex" data-testid="videos-nav-pricing">Pricing</Button></Link>
            <Link to="/get-demo"><Button variant="ghost" className="h-10 font-semibold hidden sm:inline-flex" data-testid="videos-nav-demo">Book a Demo</Button></Link>
            <Link to="/login"><Button variant="ghost" className="h-10 font-semibold" data-testid="videos-nav-login">Sign In</Button></Link>
            <Link to="/register"><Button className="h-10 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="videos-nav-register">Start Free</Button></Link>
          </div>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-5 pt-16 pb-8">
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight" data-testid="videos-heading">See It <span className="text-emerald-600">In Action</span></h1>
        <p className="mt-5 text-lg text-zinc-500 max-w-2xl">Short videos showing how teams use Orbit labels on the lot, in the shop, on the job site, and beyond.</p>
      </section>

      <section className="max-w-5xl mx-auto px-5 pb-24">
        {missing && (
          <p className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm px-4 py-3" data-testid="videos-cat-missing">
            {requested} videos are coming soon — here's everything we've published so far.
          </p>
        )}
        {cats.length > 2 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="videos-categories">
            {cats.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`videos-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${cat === c ? "bg-zinc-950 text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}>{c}</button>
            ))}
          </div>
        )}
        {loading ? <div className="py-24 text-center"><Spinner size={28} className="animate-spin text-emerald-600 mx-auto" /></div>
          : visible.length === 0 ? (
            <div className="py-24 text-center text-zinc-400" data-testid="videos-empty"><VideoCamera size={40} className="mx-auto mb-3 text-zinc-300" />Videos coming soon.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {visible.map((v) => (
                <article key={v.id} className="rounded-2xl border border-zinc-200 bg-white overflow-hidden shadow-sm" data-testid={`video-card-${v.id}`}>
                  <div className="aspect-video bg-black"><VideoPlayer video={v} testId={`video-player-${v.id}`} /></div>
                  <div className="p-6">
                    <p className="text-xs uppercase tracking-[0.18em] font-semibold text-emerald-600 mb-2">{v.category || "General"}</p>
                    <h2 className="font-display font-bold text-xl leading-snug">{v.title}</h2>
                    {v.description && <p className="text-zinc-600 mt-2 leading-relaxed text-[15px]">{v.description}</p>}
                  </div>
                </article>
              ))}
            </div>
          )}
      </section>

      <section className="bg-zinc-950 text-white">
        <div className="max-w-5xl mx-auto px-5 py-14 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Want to see it on your inventory?</h2>
            <p className="text-zinc-400 mt-2">Book a walkthrough or start free today.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/get-demo"><Button variant="outline" className="h-12 px-6 border-zinc-700 bg-transparent text-white hover:bg-zinc-900 font-semibold" data-testid="videos-cta-demo">Book a Demo</Button></Link>
            <Link to="/register"><Button className="h-12 px-6 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold gap-2" data-testid="videos-cta-start">Start Free <ArrowRight size={18} weight="bold" /></Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
